import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/Atoms/Input/input";

export const DEFAULT_FILTER_SEARCH_MAX_WIDTH_PERCENT = 45;

export interface FilterDrawerProps {
	/** Whether the filter drawer is open */
	isOpen: boolean;
	/** Current filter value */
	value: string;
	/** Callback when filter changes */
	onChange: (value: string) => void;
	/** Debounce delay in milliseconds */
	debounce?: number;
	/** Placeholder text for the search input */
	placeholder?: string;
	/** Content to show in the trailing area */
	filtersTrailingContent?: React.ReactNode;
	/** Optional test id for the search input */
	searchInputTestId?: string;
	/** Max width of the search input as a percentage of the table row @default 45 */
	searchMaxWidthPercent?: number;
}

/**
 * FilterDrawer component provides a sliding filter drawer with debounced search input.
 * Designed to be used at the route level, outside of table re-rendering cycles.
 *
 * @param isOpen - Whether the drawer is visible
 * @param value - Current filter value
 * @param onChange - Callback fired after debounce period
 * @param debounce - Debounce delay in milliseconds (default: 500)
 * @param placeholder - Placeholder text (default: "Search")
 * @param filtersTrailingContent - Content to show in the trailing area
 */
export function FilterDrawer({
	isOpen,
	value: initialValue,
	onChange,
	debounce = 500,
	placeholder = "Search",
	filtersTrailingContent,
	searchInputTestId,
	searchMaxWidthPercent = DEFAULT_FILTER_SEARCH_MAX_WIDTH_PERCENT,
}: FilterDrawerProps) {
	const [value, setValue] = useState(initialValue);
	const lastEmittedValue = useRef(initialValue);

	useEffect(() => {
		setValue(initialValue);
		lastEmittedValue.current = initialValue;
	}, [initialValue]);

	useEffect(() => {
		// Only debounce and emit if value actually changed
		if (value === lastEmittedValue.current) return;

		const timeout = setTimeout(() => {
			lastEmittedValue.current = value;
			onChange(value);
		}, debounce);

		return () => clearTimeout(timeout);
	}, [value, debounce, onChange]);

	const searchInput = (
		<Input
			data-testid={searchInputTestId}
			clearButtonTestId={
				searchInputTestId ? `${searchInputTestId}-clear-btn` : undefined
			}
			placeholder={placeholder}
			value={String(value)}
			onChange={(e) => setValue(e)}
			className="w-full"
			showClearButton={value !== ""}
		/>
	);

	return (
		<div
			className="overflow-hidden transition-all duration-300 ease-in-out"
			style={{
				maxHeight: isOpen ? "200px" : "0px",
				opacity: isOpen ? 1 : 0,
			}}
		>
			<div className="px-3 pb-4 flex items-center gap-3">
				{filtersTrailingContent ? (
					<div
						className="min-w-[200px] flex-1"
						style={{ maxWidth: `${searchMaxWidthPercent}%` }}
					>
						{searchInput}
					</div>
				) : (
					<div
						className="min-w-[200px] w-full"
						style={{ maxWidth: `${searchMaxWidthPercent}%` }}
					>
						{searchInput}
					</div>
				)}
				{filtersTrailingContent}
			</div>
		</div>
	);
}
